import {
  DEVELOPER_GROUP_LABELS,
  FUNCTION_ROLE_LABELS,
  type DeveloperGroup,
  type FunctionRole,
} from '@company/shared'
import { Plus, Trash2, Crown, UserCog } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useConfirmDialog,
  useActiveEmployees,
  useTeamMembers,
  useAddTeamMember,
  useRemoveTeamMember,
  useSetTeamLeader,
  useSetTeamGroupLeader,
  useRemoveTeamGroupLeader,
  type TeamMemberType,
  type AddTeamMemberInput,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface TeamMembersProps {
  teamId: string
  isArchived?: boolean
}

const MEMBER_TYPE_LABELS: Record<TeamMemberType, string> = {
  developer: '开发成员',
  function: '职能成员',
}

/**
 * 团队成员管理组件
 */
export function TeamMembers({ teamId, isArchived }: TeamMembersProps) {
  const [showAddForm, setShowAddForm] = useState(false)

  const { data: members, isLoading, error } = useTeamMembers(teamId)
  const removeMutation = useRemoveTeamMember()
  const setLeaderMutation = useSetTeamLeader()
  const setGroupLeaderMutation = useSetTeamGroupLeader()
  const removeGroupLeaderMutation = useRemoveTeamGroupLeader()
  const { confirmWithPreset, confirm, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleRemove = async (memberId: string) => {
    const confirmed = await confirmWithPreset(
      'remove',
      '确定要移除此成员吗？'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await removeMutation.mutateAsync({ teamId, memberId })
      } finally {
        setLoading(false)
      }
    }
  }

  const handleSetLeader = async (memberId: string) => {
    const confirmed = await confirm({
      title: '确认设置',
      description: '确定要将此成员设为团队负责人吗？',
      confirmText: '确认',
      icon: 'info',
    })
    if (confirmed) {
      setLoading(true)
      try {
        await setLeaderMutation.mutateAsync({ teamId, memberId })
      } finally {
        setLoading(false)
      }
    }
  }

  const handleToggleGroupLeader = (memberId: string, isLeader: boolean) => {
    if (isLeader) {
      removeGroupLeaderMutation.mutate({ teamId, memberId })
    } else {
      setGroupLeaderMutation.mutate({ teamId, memberId })
    }
  }

  if (isLoading) {
    return <div className="text-muted-foreground">加载中...</div>
  }

  if (error) {
    return <div className="text-destructive">加载失败: {error.message}</div>
  }

  return (
    <div className="space-y-6">
      {/* 添加成员按钮 */}
      {!isArchived && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            添加成员
          </button>
        </div>
      )}

      {/* 团队负责人 */}
      {members?.leader && (
        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-3 flex items-center gap-2 font-medium">
            <Crown className="h-4 w-4 text-yellow-500" />
            团队负责人
          </h3>
          <MemberCard
            member={members.leader}
            onRemove={!isArchived && members.leader ? () => void handleRemove(members.leader?.id ?? '') : undefined}
          />
        </div>
      )}

      {/* 开发成员 */}
      <div className="rounded-lg border bg-card p-4">
        <h3 className="mb-3 font-medium">开发成员</h3>
        {Object.entries(DEVELOPER_GROUP_LABELS).map(([group, label]) => {
          const groupMembers = members?.developers[group as DeveloperGroup] || []
          if (groupMembers.length === 0) { return null }
          return (
            <div key={group} className="mb-4 last:mb-0">
              <h4 className="mb-2 text-sm font-medium text-muted-foreground">{label}</h4>
              <div className="space-y-2">
                {groupMembers.map((member) => (
                  <MemberCard
                    key={member.id}
                    member={member}
                    showGroupLeader
                    onRemove={!isArchived ? () => { void handleRemove(member.id) } : undefined}
                    onToggleGroupLeader={!isArchived ? () => { void handleToggleGroupLeader(member.id, member.isLeader) } : undefined}
                    onSetLeader={!isArchived ? () => { void handleSetLeader(member.id) } : undefined}
                  />
                ))}
              </div>
            </div>
          )
        })}
        {Object.values(members?.developers || {}).every(arr => arr.length === 0) && (
          <p className="text-sm text-muted-foreground">暂无开发成员</p>
        )}
      </div>

      {/* 职能成员 */}
      <div className="rounded-lg border bg-card p-4">
        <h3 className="mb-3 font-medium">职能成员</h3>
        {Object.entries(FUNCTION_ROLE_LABELS).map(([role, label]) => {
          const roleMembers = members?.functions[role as FunctionRole] || []
          if (roleMembers.length === 0) { return null }
          return (
            <div key={role} className="mb-4 last:mb-0">
              <h4 className="mb-2 text-sm font-medium text-muted-foreground">{label}</h4>
              <div className="space-y-2">
                {roleMembers.map((member) => (
                  <MemberCard
                    key={member.id}
                    member={member}
                    onRemove={!isArchived ? () => void handleRemove(member.id) : undefined}
                  />
                ))}
              </div>
            </div>
          )
        })}
        {Object.values(members?.functions || {}).every(arr => arr.length === 0) && (
          <p className="text-sm text-muted-foreground">暂无职能成员</p>
        )}
      </div>

      {/* 添加成员表单 */}
      {showAddForm && (
        <AddMemberForm
          teamId={teamId}
          onClose={() => setShowAddForm(false)}
          onSuccess={() => setShowAddForm(false)}
        />
      )}
      <ConfirmDialog />
    </div>
  )
}

interface MemberCardProps {
  member: {
    id: string
    employeeId: string
    isLeader: boolean
    memberType: TeamMemberType
    functionRole: FunctionRole | null
    developerGroup: DeveloperGroup | null
    employee?: {
      id: string
      name: string
      email: string
      avatar?: string | null
    }
  }
  showGroupLeader?: boolean
  onRemove?: () => void
  onToggleGroupLeader?: () => void
  onSetLeader?: () => void
}

function MemberCard({ member, showGroupLeader, onRemove, onToggleGroupLeader, onSetLeader }: MemberCardProps) {
  const displayName = member.employee?.name || `员工 ${member.employeeId.slice(0, 8)}`
  const initials = member.employee?.name?.slice(0, 1) || member.employeeId.slice(0, 2).toUpperCase()

  return (
    <div className="flex items-center justify-between rounded-md border bg-background p-3">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium">
          {initials}
        </div>
        <div>
          <p className="text-sm font-medium">
            {displayName}
            {member.isLeader && showGroupLeader && (
              <span className="ml-2 text-xs text-yellow-600">组长</span>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {member.memberType === 'developer'
              ? DEVELOPER_GROUP_LABELS[member.developerGroup ?? 'frontend']
              : FUNCTION_ROLE_LABELS[member.functionRole ?? 'hr']}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {onSetLeader && member.memberType === 'developer' && (
          <button
            onClick={onSetLeader}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="设为团队负责人"
          >
            <Crown className="h-4 w-4" />
          </button>
        )}
        {onToggleGroupLeader && showGroupLeader && (
          <button
            onClick={onToggleGroupLeader}
            className={cn(
              'rounded-md p-1.5 hover:bg-muted',
              member.isLeader ? 'text-yellow-600' : 'text-muted-foreground hover:text-foreground'
            )}
            title={member.isLeader ? '取消组长' : '设为组长'}
          >
            <UserCog className="h-4 w-4" />
          </button>
        )}
        {onRemove && (
          <button
            onClick={onRemove}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title="移除成员"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

interface AddMemberFormProps {
  teamId: string
  onClose: () => void
  onSuccess: () => void
}

function AddMemberForm({ teamId, onClose, onSuccess }: AddMemberFormProps) {
  const addMutation = useAddTeamMember()
  const { data: employees, isLoading: employeesLoading } = useActiveEmployees()
  const [formData, setFormData] = useState<AddTeamMemberInput>({
    employeeId: '',
    memberType: 'developer',
    developerGroup: 'frontend',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.employeeId) {
      newErrors.employeeId = '请选择员工'
    }
    if (formData.memberType === 'developer' && !formData.developerGroup) {
      newErrors.developerGroup = '请选择开发组'
    }
    if (formData.memberType === 'function' && !formData.functionRole) {
      newErrors.functionRole = '请选择职能类型'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) { return }

    const data: AddTeamMemberInput = {
      employeeId: formData.employeeId,
      memberType: formData.memberType,
    }
    if (formData.memberType === 'developer') {
      data.developerGroup = formData.developerGroup
    } else {
      data.functionRole = formData.functionRole
    }

    addMutation.mutate(
      { teamId, data },
      {
        onSuccess: () => {
          onSuccess()
          onClose()
        },
      }
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
        <h3 className="mb-4 text-lg font-semibold">添加成员</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 选择员工 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">
              选择员工 <span className="text-destructive">*</span>
            </label>
            <Select
              value={formData.employeeId}
              onValueChange={(value) => setFormData({ ...formData, employeeId: value })}
            >
              <SelectTrigger className={cn(errors.employeeId && 'border-destructive')}>
                <SelectValue placeholder={employeesLoading ? '加载中...' : '选择员工'} />
              </SelectTrigger>
              <SelectContent>
                {employees?.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name} {emp.employeeNo ? `(${emp.employeeNo})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.employeeId && (
              <p className="mt-1 text-sm text-destructive">{errors.employeeId}</p>
            )}
          </div>

          {/* 成员类型 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">成员类型</label>
            <Select
              value={formData.memberType}
              onValueChange={(value) => {
                const type = value as TeamMemberType
                setFormData({
                  ...formData,
                  memberType: type,
                  developerGroup: type === 'developer' ? 'frontend' : undefined,
                  functionRole: type === 'function' ? 'finance' : undefined,
                })
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(MEMBER_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 开发组 */}
          {formData.memberType === 'developer' && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                开发组 <span className="text-destructive">*</span>
              </label>
              <Select
                value={formData.developerGroup || ''}
                onValueChange={(value) => setFormData({ ...formData, developerGroup: value as DeveloperGroup })}
              >
                <SelectTrigger className={cn(errors.developerGroup && 'border-destructive')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DEVELOPER_GROUP_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.developerGroup && (
                <p className="mt-1 text-sm text-destructive">{errors.developerGroup}</p>
              )}
            </div>
          )}

          {/* 职能类型 */}
          {formData.memberType === 'function' && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                职能类型 <span className="text-destructive">*</span>
              </label>
              <Select
                value={formData.functionRole || ''}
                onValueChange={(value) => setFormData({ ...formData, functionRole: value as FunctionRole })}
              >
                <SelectTrigger className={cn(errors.functionRole && 'border-destructive')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FUNCTION_ROLE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.functionRole && (
                <p className="mt-1 text-sm text-destructive">{errors.functionRole}</p>
              )}
            </div>
          )}

          {/* 错误提示 */}
          {addMutation.error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {addMutation.error.message}
            </div>
          )}

          {/* 按钮 */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={addMutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {addMutation.isPending ? '添加中...' : '添加'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
